/**
 * Customer invoice worklist — `/finance/customer-invoices`.
 *
 * `listCustomerInvoices` returns headers only and publishes no cursor — the
 * contract says a worklist never renders lines and a page of a hundred
 * invoices' lines is a payload nobody consumes. So this screen is filtered,
 * not paged, and the lines live on the detail read.
 *
 * A draft carries no invoice number: the branch legal series is allocated at
 * finalization, so an abandoned draft never burns a sequence value. The number
 * column says so instead of showing an empty cell.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Button, Card, CardContent } from "@erp/ui";
import { Plus } from "lucide-react";

import { DataTable, badgeCell, moneyCell } from "@/components/data-table";

import { AR_INVOICE_STATUSES, type CustomerInvoice, type CustomerInvoiceListQuery } from "../api";
import { PageHeader, SelectField } from "../components/fields";
import { useCustomerInvoiceList, usePartyOptions } from "../queries";

const ALL = "__all__";

export const AR_INVOICE_STATUS_VARIANTS = {
  draft: "outline",
  posted: "default",
  partially_paid: "warning",
  paid: "success",
  void: "destructive",
} as const;

export interface CustomerInvoicesPageProps {
  onOpenInvoice: (invoiceId: string) => void;
  onCreateInvoice: () => void;
  canCreate?: boolean;
  canReadParties?: boolean;
}

export function CustomerInvoicesPage({
  onOpenInvoice,
  onCreateInvoice,
  canCreate = true,
  canReadParties = true,
}: CustomerInvoicesPageProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>(ALL);
  const [partyId, setPartyId] = useState<string>(ALL);

  const filters = useMemo<CustomerInvoiceListQuery>(() => {
    const built: { status?: CustomerInvoiceListQuery["status"]; party_id?: string } = {};
    if (status !== ALL) built.status = status as NonNullable<CustomerInvoiceListQuery>["status"];
    if (partyId !== ALL) built.party_id = partyId;
    return built;
  }, [status, partyId]);

  const listQuery = useCustomerInvoiceList(filters);
  const partiesQuery = usePartyOptions("customer", canReadParties);
  const parties = useMemo(() => partiesQuery.data ?? [], [partiesQuery.data]);
  const partyName = (id: string): string =>
    parties.find((party) => party.id === id)?.display_name ?? id;

  const statusLabel = (value: string): string =>
    t(`finance.customerInvoices.status.${value}`, value);

  const columns = useMemo<ColumnDef<CustomerInvoice>[]>(
    () => [
      {
        accessorKey: "invoice_no",
        id: "invoice_no",
        header: t("finance.customerInvoices.columns.invoiceNo", "Number"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return value === "" ? (
            <span className="text-muted-foreground">
              {t("finance.customerInvoices.numberAtFinalize", "Allocated at finalization")}
            </span>
          ) : (
            <span dir="ltr" className="font-mono font-medium">
              {value}
            </span>
          );
        },
      },
      {
        accessorKey: "party_id",
        id: "party_id",
        header: t("finance.customerInvoices.columns.customer", "Customer"),
        enableSorting: false,
        cell: (ctx) => partyName(ctx.getValue<string>()),
      },
      {
        accessorKey: "issue_date",
        id: "issue_date",
        header: t("finance.customerInvoices.columns.issueDate", "Issued"),
        enableSorting: false,
        cell: (ctx) => <span dir="ltr">{ctx.getValue<string>()}</span>,
      },
      {
        accessorKey: "due_date",
        id: "due_date",
        header: t("finance.customerInvoices.columns.dueDate", "Due"),
        enableSorting: false,
        cell: (ctx) => <span dir="ltr">{ctx.getValue<string>()}</span>,
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("finance.customerInvoices.columns.status", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(value, AR_INVOICE_STATUS_VARIANTS, statusLabel(value));
        },
      },
      {
        accessorKey: "gross_total",
        id: "gross_total",
        header: t("finance.customerInvoices.columns.grossTotal", "Gross"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => moneyCell(ctx.row.original.gross_total),
      },
      {
        accessorKey: "open_amount",
        id: "open_amount",
        header: t("finance.customerInvoices.columns.openAmount", "Open"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => moneyCell(ctx.row.original.open_amount),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- helpers close over stable query data
    [t, parties],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.customerInvoices.title", "Customer invoices")}
        description={t(
          "finance.customerInvoices.subtitle",
          "Drafts are editable; everything past draft is an issued legal document.",
        )}
        actions={
          canCreate ? (
            <Button type="button" onClick={onCreateInvoice}>
              <Plus className="size-4" aria-hidden="true" />
              {t("finance.customerInvoices.create", "New invoice")}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <SelectField
            id="customer-invoice-status"
            label={t("finance.customerInvoices.filterStatus", "Status")}
            value={status}
            onValueChange={setStatus}
            className="w-52"
            options={[
              { value: ALL, label: t("finance.common.all", "All") },
              ...AR_INVOICE_STATUSES.map((value) => ({ value, label: statusLabel(value) })),
            ]}
          />
          <SelectField
            id="customer-invoice-party"
            label={t("finance.customerInvoices.filterCustomer", "Customer")}
            value={partyId}
            onValueChange={setPartyId}
            className="w-72"
            disabled={!canReadParties}
            options={[
              { value: ALL, label: t("finance.common.all", "All") },
              ...parties.map((party) => ({ value: party.id, label: party.display_name })),
            ]}
          />
        </CardContent>
      </Card>

      <DataTable
        columns={columns}
        rows={listQuery.data ?? []}
        getRowId={(row) => row.id}
        onRowClick={(row) => onOpenInvoice(row.id)}
        storageKey="finance.customerInvoices.columns"
        emptyMessage={
          listQuery.isError
            ? t("finance.customerInvoices.loadError", "Customer invoices could not be loaded.")
            : t("finance.customerInvoices.empty", "No customer invoices match these filters.")
        }
      />
    </div>
  );
}
